package org.dronda.app.web.ui.events

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import org.dronda.app.web.Loader
import org.dronda.app.web.ui.components.AppColors
import org.dronda.app.web.ui.components.Badge
import org.dronda.app.web.ui.components.Card
import org.dronda.app.web.ui.components.Details
import org.dronda.app.web.ui.components.PageColumn
import org.dronda.app.web.ui.components.Summary
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun EventsScreen(
    viewModel: EventsViewModel,
    onEventClick: (String) -> Unit,
    onCreateClick: () -> Unit,
) {
    val uiState by viewModel.uiState.collectAsState()

    PageColumn {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            H1(Modifier.fontWeight(FontWeight.Light).toAttrs()) {
                Text("Events")
            }
            Span(
                Modifier
                    .fontSize(2.cssRem)
                    .cursor(Cursor.Pointer)
                    .onClick { onCreateClick() }
                    .toAttrs()
            ) {
                Text("+")
            }
        }

        when (val state = uiState) {
            is EventsViewModel.UiState.Loading -> {
                Loader()
            }
            is EventsViewModel.UiState.Success -> {
                if (state.upcoming.isEmpty() && state.past.isEmpty()) {
                    EmptyState()
                } else {
                    if (state.upcoming.isEmpty()) {
                        NoUpcomingEvents()
                    } else {
                        EventsList(state.upcoming, onEventClick)
                    }
                    if (state.past.isNotEmpty()) {
                        PastEvents(state.past, onEventClick)
                    }
                }
            }
            is EventsViewModel.UiState.Error -> {
                Text(state.message)
            }
        }
    }
}

@Composable
private fun EmptyState() {
    Card(Modifier.padding(2.cssRem)) {
        P(
            Modifier
                .color(AppColors.muted)
                .fontSize(1.cssRem)
                .toAttrs()
        ) {
            Text("No events yet. Create your first event using the + button above!")
        }
    }
}

@Composable
private fun NoUpcomingEvents() {
    P(
        Modifier
            .color(AppColors.muted)
            .fontSize(1.cssRem)
            .toAttrs()
    ) {
        Text("No upcoming events")
    }
}

@Composable
private fun PastEvents(events: List<EventsViewModel.EventCardDisplay>, onEventClick: (String) -> Unit) {
    Details(Modifier.fillMaxWidth().margin(top = 1.5.cssRem).toAttrs()) {
        Summary(
            Modifier
                .cursor(Cursor.Pointer)
                .color(AppColors.muted)
                .margin(bottom = 0.5.cssRem)
                .toAttrs()
        ) {
            Text("Past events (${events.size})")
        }
        EventsList(events, onEventClick)
    }
}

@Composable
private fun EventsList(events: List<EventsViewModel.EventCardDisplay>, onEventClick: (String) -> Unit) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(0.5.cssRem)
    ) {
        events.forEach { event ->
            Card(onClick = { onEventClick(event.id) }) {
                Row(Modifier.gap(0.75.cssRem), verticalAlignment = Alignment.CenterVertically) {
                    H3(Modifier.fontWeight(FontWeight.Normal).toAttrs()) {
                        Text(event.name)
                    }
                    event.badge?.let { Badge(it) }
                }
                Text(event.date)
            }
        }
    }
}
